import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RolesService } from '../roles.service';
import { ToolbarFilter } from '../../../shared/components/page-toolbar/page-toolbar.component';
import { TableColumn, TableRow } from '../../../shared/components/data-table/data-table.component';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';
import { Subscription } from 'rxjs';
import { AuthStateService } from '../../../core/authentication/auth-state.service';

@Component({
  selector: 'app-role-list',
  standalone: false,
  templateUrl: './role-list.component.html',
  styleUrl: './role-list.component.scss',
})
export class RoleListComponent implements OnInit, OnDestroy {
  private rolesService = inject(RolesService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authState = inject(AuthStateService);
  private _subs: Subscription[] = [];

  readonly createRoleAction = new EventEmitter<void>();
  readonly auditLogAction = new EventEmitter<void>();

  currentPage = 1;
  itemsPerPage = 50;
  totalResults = 0;
  isLoading = false;

  searchQuery = '';
  selectedStatus = '';

  get headerActions(): HeaderAction[] {
    const actions: HeaderAction[] = [];
    if (this.authState.hasPermission('reports.view')) {
      actions.push({ label: 'Audit log', variant: 'ghost', emitter: this.auditLogAction });
    }
    if (this.authState.hasPermission('roles.create')) {
      actions.push({ label: '+ Create role', variant: 'primary', emitter: this.createRoleAction });
    }
    return actions;
  }

  filters: ToolbarFilter[] = [
    { key: 'status', label: 'Status', options: ['Active', 'Inactive'] },
  ];

  roleColumns: TableColumn[] = [
    { key: 'role', label: 'Role', type: 'roleCell' },
    { key: 'permissionScope', label: 'Permission scope', type: 'tags' },
    { key: 'linkedUsers', label: 'Linked users', type: 'linkedUsers' },
    { key: 'status', label: 'Status', type: 'status' },
    { key: 'lastUpdated', label: 'Last updated', type: 'text' },
  ];

  roleRows: TableRow[] = [];

  ngOnInit(): void {
    this._subs.push(
      this.createRoleAction.subscribe(() =>
        this.router.navigate(['create'], { relativeTo: this.route })
      ),
      this.auditLogAction.subscribe(() =>
        this.router.navigate(['/audit-log'])
      ),
    );
    this.loadRoles();
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
  }

  loadRoles(): void {
    this.isLoading = true;

    let statusFilter: string | undefined;
    if (this.selectedStatus === 'Active') statusFilter = 'active';
    else if (this.selectedStatus === 'Inactive') statusFilter = 'inactive';

    this.rolesService
      .findAll({
        page: this.currentPage,
        limit: this.itemsPerPage,
        search: this.searchQuery || undefined,
        status: statusFilter,
      })
      .subscribe({
        next: (response) => {
          this.roleRows = response.data.map((role) => ({
            id: role.id,
            role: {
              iconType: 'shield',
              name: role.name,
              description: '',
              tone: role.status === 'active' ? 'green' : 'gray',
            },
            permissionScope: role.permissionsCount,
            linkedUsers: {
              count: role.usersCount,
              icon: role.usersCount > 0 ? 'user' : 'none',
            },
            status: role.status === 'active' ? 'Active' : 'Inactive',
            lastUpdated: role.updated_at
              ? new Date(role.updated_at).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : '—',
          }));
          this.totalResults = response.total;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load roles:', err);
          this.isLoading = false;
        },
      });
  }

  onHeaderAction(_action: any): void { /* handled via per-button emitters */ }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadRoles();
  }

  onFilterChange(filter: ToolbarFilter): void {
    if (filter.key === 'status') this.selectedStatus = filter.value || '';
    this.currentPage = 1;
    this.loadRoles();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadRoles();
  }

  onItemsPerPageChange(items: number): void {
    this.itemsPerPage = items;
    this.currentPage = 1;
    this.loadRoles();
  }

  onRowClick(row: TableRow): void {
    this.router.navigate(['/roles', row.id]);
  }
}
